import type { Event, EventHint } from '@sentry/react';

const FETCH_FAILURE_WITH_HOST = /^(?:Failed to fetch|Load failed) \(([^)]+)\)$/i;
const FETCH_FAILURE_WITHOUT_HOST = /^(?:Failed to fetch|Load failed)$/i;

// Browser privacy tools commonly block these optional observability and analytics requests.
// Keep this list narrow so application network failures still reach Sentry.
const NOISY_FETCH_HOST_PATTERNS = [/^cdn\.segment\.com$/i, /^browser-intake-datadoghq\./i];

function isConsoleOriginEvent(event: Event) {
    if (event.logger === 'console') {
        return true;
    }

    const exceptionValues = event.exception?.values ?? [];
    return exceptionValues.some((value) => value.mechanism?.type === 'console');
}

function getEventMessage(event: Event, hint?: EventHint) {
    return event.message ?? event.logentry?.message ?? hint?.originalException?.toString?.() ?? '';
}

function isNoisyFetchHost(host: string) {
    return NOISY_FETCH_HOST_PATTERNS.some((pattern) => pattern.test(host));
}

function getHost(url: unknown) {
    if (typeof url !== 'string') {
        return null;
    }

    try {
        return new URL(url).hostname;
    } catch {
        return null;
    }
}

function nearestFailedFetchIsNoisy(event: Event) {
    const breadcrumbs = event.breadcrumbs ?? [];
    for (let index = breadcrumbs.length - 1; index >= 0; index -= 1) {
        const breadcrumb = breadcrumbs[index];
        if (breadcrumb.category !== 'fetch' || breadcrumb.level !== 'error') {
            continue;
        }

        const host = getHost(breadcrumb.data?.url);
        return host !== null && isNoisyFetchHost(host);
    }

    return false;
}

export function shouldDropSentryEvent(event: Event, hint?: EventHint) {
    if (!isConsoleOriginEvent(event)) {
        return false;
    }

    const message = getEventMessage(event, hint);
    const hostMatch = FETCH_FAILURE_WITH_HOST.exec(message);
    if (hostMatch) {
        return isNoisyFetchHost(hostMatch[1]?.trim() ?? '');
    }

    return FETCH_FAILURE_WITHOUT_HOST.test(message) && nearestFailedFetchIsNoisy(event);
}
